import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { AdminLayout } from '../components/AdminLayout.js'
import { Modal } from '../components/Modal.js'
import { CopyButton } from '../components/CopyButton.js'
import { AdminApiError } from '../adminApi.js'
import { useAdminMe } from '../hooks/useAdminMe.js'
import {
  useAdminsList,
  useCreateAdmin,
  useDeactivateAdmin,
  useReactivateAdmin,
} from '../hooks/useAdmins.js'
import { fmtDate, fmtDateTime } from '../format.js'

/** Generates a strong 18-char temp password for the "generate" helper. */
function generatePassword(): string {
  const chars =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%'
  const bytes = new Uint8Array(18)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => chars[b % chars.length]).join('')
}

interface CreateAdminForm {
  email: string
  name: string
  temporary_password: string
}

export function Admins() {
  const me = useAdminMe()
  const list = useAdminsList()
  const createAdmin = useCreateAdmin()
  const deactivate = useDeactivateAdmin()
  const reactivate = useReactivateAdmin()

  const [createOpen, setCreateOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateAdminForm>()

  const myId = me.data?.admin.id
  const admins = list.data?.admins ?? []
  const activeCount = admins.filter((a) => a.is_active).length

  function onCreate(values: CreateAdminForm) {
    createAdmin.mutate(values, {
      onSuccess: (res) => {
        toast.success(
          `Admin ${res.admin.email} created — share the temporary password.`,
        )
        setCreateOpen(false)
        reset()
      },
      onError: (err) =>
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : 'Could not create admin.',
        ),
    })
  }

  function handleDeactivate(id: string, email: string) {
    deactivate.mutate(id, {
      onSuccess: () => toast.success(`${email} deactivated.`),
      onError: (err) =>
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : 'Could not deactivate admin.',
        ),
    })
  }

  function handleReactivate(id: string, email: string) {
    reactivate.mutate(id, {
      onSuccess: () => toast.success(`${email} reactivated.`),
      onError: (err) =>
        toast.error(
          err instanceof AdminApiError
            ? err.userMessage
            : 'Could not reactivate admin.',
        ),
    })
  }

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <h1>Admins</h1>
        <button
          type="button"
          className="admin-btn admin-btn-primary"
          onClick={() => {
            reset()
            setCreateOpen(true)
          }}
        >
          Create admin
        </button>
      </div>

      {list.isLoading ? (
        <p className="admin-audit-meta">Loading admins…</p>
      ) : list.isError ? (
        <div className="admin-error-state">
          <p>Couldn't load admins.</p>
          <button
            type="button"
            className="admin-btn"
            onClick={() => void list.refetch()}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="admin-card" style={{ padding: 0 }}>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Created</th>
                  <th>Last login</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => {
                  const isSelf = a.id === myId
                  // Disable deactivate for self and for the last active admin.
                  const isLastActive = a.is_active && activeCount <= 1
                  const deactivateDisabled = isSelf || isLastActive
                  return (
                    <tr key={a.id} style={{ cursor: 'default' }}>
                      <td data-label="Email">{a.email}</td>
                      <td data-label="Name">{a.name}</td>
                      <td className="admin-audit-meta" data-label="Created">
                        {fmtDate(a.created_at)}
                      </td>
                      <td
                        className="admin-audit-meta"
                        data-label="Last login"
                      >
                        {fmtDateTime(a.last_login_at)}
                      </td>
                      <td data-label="Status">
                        {a.is_active ? (
                          <span className="admin-badge admin-badge-ok">
                            Active
                          </span>
                        ) : (
                          <span className="admin-badge admin-badge-neutral">
                            Inactive
                          </span>
                        )}
                        {a.must_change_password && (
                          <span
                            className="admin-badge admin-badge-warn"
                            style={{ marginLeft: 6 }}
                          >
                            Temp pw
                          </span>
                        )}
                      </td>
                      <td data-label="Actions">
                        {a.is_active ? (
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm admin-btn-danger"
                            disabled={
                              deactivateDisabled || deactivate.isPending
                            }
                            title={
                              isSelf
                                ? 'You cannot deactivate your own account'
                                : isLastActive
                                  ? 'Cannot deactivate the last active admin'
                                  : undefined
                            }
                            onClick={() =>
                              handleDeactivate(a.id, a.email)
                            }
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            disabled={reactivate.isPending}
                            onClick={() =>
                              handleReactivate(a.id, a.email)
                            }
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && (
        <Modal title="Create admin" onClose={() => setCreateOpen(false)}>
          <form onSubmit={handleSubmit(onCreate)} noValidate>
            <div className="admin-field">
              <label className="admin-label" htmlFor="admin-email">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                className="admin-input"
                autoComplete="off"
                aria-invalid={errors.email ? 'true' : 'false'}
                {...register('email', { required: 'Email is required' })}
              />
              {errors.email && (
                <p className="admin-field-error">{errors.email.message}</p>
              )}
            </div>

            <div className="admin-field">
              <label className="admin-label" htmlFor="admin-name">
                Name
              </label>
              <input
                id="admin-name"
                className="admin-input"
                aria-invalid={errors.name ? 'true' : 'false'}
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && (
                <p className="admin-field-error">{errors.name.message}</p>
              )}
            </div>

            <div className="admin-field">
              <label className="admin-label" htmlFor="admin-temp-pw">
                Temporary password
              </label>
              <div className="admin-copy-row">
                <input
                  id="admin-temp-pw"
                  className="admin-input"
                  aria-invalid={errors.temporary_password ? 'true' : 'false'}
                  {...register('temporary_password', {
                    required: 'Temporary password is required',
                    minLength: {
                      value: 12,
                      message: 'At least 12 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  className="admin-btn admin-btn-sm"
                  onClick={() =>
                    setValue('temporary_password', generatePassword(), {
                      shouldValidate: true,
                    })
                  }
                >
                  Generate
                </button>
              </div>
              {errors.temporary_password && (
                <p className="admin-field-error">
                  {errors.temporary_password.message}
                </p>
              )}
              <p className="admin-hint">
                The new admin must change this on first login. Copy it and
                relay it to them — it isn't shown again.
              </p>
              {watch('temporary_password') && (
                <div style={{ marginTop: 8 }}>
                  <CopyButton
                    value={watch('temporary_password')}
                    label="Copy password"
                  />
                </div>
              )}
            </div>

            <div className="admin-form-actions">
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={createAdmin.isPending}
              >
                {createAdmin.isPending ? 'Creating…' : 'Create admin'}
              </button>
              <button
                type="button"
                className="admin-btn"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  )
}
